/**
 * **The home's entry point into the attention queue** — one read in, two cards' worth of rows out,
 * plus the honesty obligation R18 puts on an empty one.
 *
 * Everything a card would otherwise have to know is settled here: which item belongs in which
 * section, what each row says, what its control does, and what to do with a
 * reason this build has no name for.
 *
 * Pure: no React, no DOM, no query hook, no i18next. What leaves is i18n KEYS — with one
 * exception, `waitingTileMeta`, which composes two COUNTED keys into one sentence and so has to
 * resolve them. It takes `t` as a parameter, which is the rule for a `model/` that needs words.
 *
 * ── The sections are the CATEGORY, not a reason list (R14) ───────────────────────────────────
 * `Feedback` is review debt — a client finished something and is waiting for a reaction — and goes
 * to "Szybki feedback". `Triage` is work that needs the trainer to intervene and goes to "Zadania".
 * `Win` is skipped — a reached goal already reaches the trainer through `GET /trainer/roster-wins`,
 * the activity list at the foot of the screen — with ONE exception, the personal record, which R24
 * makes a task like any other on every surface (owner, 2 October 2026). `taskSectionOf` is that
 * decision, taken once for the Pulpit, `/zadania` and the client's Przegląd.
 * Reading the category off the item rather than enumerating reasons is R14's own instruction, and
 * it means a reason the backend adds later classifies itself.
 *
 * ── Arrival order is preserved, and that is not laziness ─────────────────────────────────────
 * The backend ranks the whole queue by severity and then by recency (R8, `RankAndCap`), so a stable
 * partition into two lists leaves each one ranked. Re-sorting here would be a second implementation
 * of a rule that already has one, and the two would eventually disagree about a tie.
 *
 * ── A reason this build cannot name is DROPPED, and the catalogue is now fully named ─────────
 * `attentionReasonOf` is the entity's own table of reasons this app has strings for. Three live
 * catalogue reasons used to sit outside it — `PlanCompleted` and the two clip reasons — so those
 * items reached no card and the tile over the bento printed fewer than the queue held. They were
 * named on 15 September 2026 (R16, R17) and every reason `AttentionReasons.All` registers with an
 * anchor prefix now reaches a card. The drop remains, because a reason the backend adds NEXT still
 * has no words; what it no longer hides is anything that ships today.
 *
 * ── The row's SENTENCE is mobile's where mobile has one ──────────────────────────────────────
 * `clients.tasks.reasons.<reason>.sub` already says what one item is about, in the words the phone
 * uses, with its own `subEmpty` / `subNoWhen` variants for the fields the wire may omit. This module
 * chooses among them — it does not write a thirteenth sentence — so the two apps describe the same
 * item identically and a re-sync of the bundle carries both forward.
 *
 * ── …except that mobile's `sub` is HALF a sentence, and this row has no other half ───────────
 * Corrected 16 September 2026, on the owner's report. The phone draws two lines — the reason's
 * title, then its `sub` under it — so "Wysłane 9 dni temu" is the tail of "Oczekujące zaproszenie".
 * This row puts the CLIENT'S NAME where that title was, and the tail then hangs off a name and says
 * nothing about what was sent. Twelve reasons were audited; the ones whose sentence could not stand
 * alone were reworded in `web:home.task.<reason>.*`, where each states the event whole and drops the
 * plan / session / goal name that was never carrying it.
 *
 * WHICH of the two scopes a reason reads from is one decision, taken in `entities/client`'s
 * `attentionReasonKey` rather than branched on here — and that module's header carries the rule
 * every web sentence obeys, starting with: it must be genderless, because this app does not know
 * the client's gender.
 *
 * ── `lapsedActivity` says HOW LONG, and the number comes from the roster ─────────────────────
 * The queue's payload carries no date for that reason, so the sentence used to fall back to a
 * `detail` that named no number at all — "Klient nie trenował od dłuższego czasu", which is both
 * vague and, at 59 characters in a 159px box, the one sentence on the screen that did not fit. The
 * roster the home already reads knows `lastWorkoutAt`, so the day count arrives through
 * `deps.daysSinceLastWorkout` and the row states it: "Bez treningu od 41 dni". A client with no
 * completed workout at all has no count, and the `detail` line says THAT instead.
 */

import {
  attentionReasonKey,
  attentionReasonOf,
  attentionReasonTitleKey,
  type AttentionReason,
} from '~/entities/client';
import type {
  TrainWithMeModulesAttentionDtosResponsesAttentionItemPayload as AttentionItemPayload,
  TrainWithMeModulesAttentionDtosResponsesAttentionQueueItem as AttentionQueueItem,
  TrainWithMeModulesAttentionDtosResponsesAttentionQueueResponse as AttentionQueueResponse,
} from '~/shared/api/generated/model';

import type { Translate } from '~/shared/lib/localeTranslate';

import { feedbackShape, type FeedbackDetail } from './feedbackShape';
import { triageShape, type TriageFacts } from './triageShape';
import { taskAction, taskGlyph, type TaskAction, type TaskGlyph } from './taskAction';

/**
 * How many dots the "Czeka na Ciebie" tile draws.
 *
 * A property of that TILE's drawing — five dots is what fits beside its figure — and no longer a
 * statement about either card's capacity. It used to be `TASKS_SHOWN`, which read as "one dot per
 * task the card can hold"; both cards draw everything now, so the two facts have come apart.
 */
const TILE_DOTS = 5;

/**
 * A sentence to translate: candidate keys, most specific first, and the values they interpolate.
 *
 * A LIST rather than one key because i18next resolves an array by taking the first key that exists,
 * which is exactly the fallback this needs — the variants differ per reason (`subEmpty` exists for
 * four of the twelve, `subNoWhen` for one) and a module that had to know which would be a second
 * copy of the locale file.
 */
export interface AttentionSentence {
  /**
   * Most specific first, ending in a key that always exists.
   *
   * `readonly` here and spread at the call site: i18next's own `t` takes a mutable `string[]`, and
   * widening this to match it would let a component reorder the fallback chain the model chose.
   */
  readonly keys: readonly string[];
  readonly values: Readonly<Record<string, string | number>>;
  /**
   * For each candidate in `keys`, in the same order, the value names THAT candidate interpolates.
   *
   * A surface that has to know what the sentence said — `pages/tasks`' fact tiles, which are
   * dropped when they would repeat it — cannot read `values`: the list is what was AVAILABLE, not
   * what was used. `detail` is always the last candidate and interpolates nothing, so a bundle
   * missing a `sub` string renders a sentence that names no field, and a rule reading `values`
   * would go on hiding the tile that is then the only place the fact appears.
   */
  readonly slots: readonly (readonly string[])[];
}

/** One row of either card. */
export interface AttentionItem {
  /** React's key. The anchor plus the reason, because two reasons may share a weekly anchor. */
  readonly key: string;
  readonly anchorKey: string;
  readonly reason: AttentionReason;
  /** The reason's own name, for the tile's accessible name — never colour alone. */
  readonly titleKey: string;
  /** `null` for the pending invitation, whose invitee is not a client yet (R10). */
  readonly subjectCustomerId: number | null;
  /** The card may offer the chat: it names a client, and not the trainer's own profile. */
  readonly messageable: boolean;
  /**
   * The session this row is ABOUT, or `null` where the wire named none.
   *
   * Two detectors put it on the payload — `SessionAwaitingReviewDetector` and
   * `AbandonedWorkoutDetector` — and it is what `model/sessionLink` turns into an address. It is not
   * the composer's id (`model/taskAction` lifts that for its own reasons) and it is not the chip
   * read's (`model/feedbackShape`, for the Feedback card's shape); the three are separate decisions
   * about the same wire field, and each states its own.
   */
  readonly workoutExecutionId: number | null;
  /**
   * When the signal behind the item happened, or `null` where the wire carried no stamp.
   *
   * The feedback tiles print its HOUR: everything in that card arrived recently enough that the
   * hour is what separates one tile from the next. `null` is a real absence and the tile prints
   * nothing rather than an epoch.
   */
  readonly signalAtUtc: string | null;
  readonly fullName: string;
  readonly initials: string;
  readonly avatarUrl: string | null;
  readonly description: AttentionSentence;
  readonly glyph: TaskGlyph;
  readonly action: TaskAction;
  /**
   * The facts the Szybki feedback card's own shape draws, already lifted off the payload — `plain`
   * for every Triage row and for any Feedback reason this build has no shape for. It lives on every
   * item rather than on a second list so that no component anywhere holds an `AttentionItemPayload`.
   */
  readonly detail: FeedbackDetail;
  /**
   * The facts a TRIAGE reason carries, already lifted off the payload — the mirror of `detail` on
   * the other half of the queue, and on every item for the same reason: no component anywhere holds
   * an `AttentionItemPayload`. Every field is `null` on a row whose reason does not carry it.
   */
  readonly triage: TriageFacts;
}

/**
 * What the "Zadania" tile over the bento prints: the figure, the two halves its sentence names, and
 * how many of its five dots are filled.
 *
 * It lives here rather than in the tile because it is a DECISION about the queue — that the tile's
 * number is both sections together, that the dots are capped at what the tall card can draw — and a
 * decision taken in JSX is one no test can reach.
 */
export interface AttentionTileFigure {
  /** Everything waiting: triage and review debt in one number, which is what the tile shows. */
  readonly total: number;
  readonly triage: number;
  readonly feedback: number;
  /** Filled dots, capped at the five the tile has room for. */
  readonly dots: number;
}

/** Between the tile's two halves. Punctuation, not copy — the same in either language. */
const META_SEPARATOR = ', ';

/**
 * The first tile's sentence: how much of what is waiting is review debt, and how much is triage.
 *
 * One key interpolating two raw counts into one sentence cannot agree with either of them — Polish
 * picks a form per NUMBER, so a single string printed "2 feedbacku, 1 zadań" and was wrong twice.
 * Each half is its own counted key, its own language selects its own form, and the join is
 * punctuation. The composition sits here rather than in the tile because a sentence assembled in
 * JSX is one no test can reach, which is how the ungrammatical version shipped.
 */
export function waitingTileMeta(figure: AttentionTileFigure, t: Translate): string {
  return [
    t('web:home.tiles.waiting.feedback', { count: figure.feedback }),
    t('web:home.tiles.waiting.tasks', { count: figure.triage }),
  ].join(META_SEPARATOR);
}

export interface AttentionSections {
  /**
   * **Every** Triage row, in the backend's own order. The card draws them all and scrolls.
   *
   * It was cut to five here until 16 September 2026. A card whose scroller holds exactly what it
   * shows cannot scroll, and the footer's way to "the rest" pointed at the roster, which answers a
   * different question.
   */
  readonly tasks: readonly AttentionItem[];
  /** How many Triage items there are. The same as `tasks.length`, and the count the head prints. */
  readonly tasksTotal: number;
  /** **Every** Feedback row. See `tasks`. */
  readonly feedback: readonly AttentionItem[];
  readonly feedbackTotal: number;
  /**
   * How many DIFFERENT people the Feedback rows are about. One client with three finished
   * sessions is three rows and one person, and the headline says "osób" — counting rows told a
   * trainer with five clients that nine people were waiting. A row with no client behind it
   * counts as its own person, so the figure never drops below what is really waiting.
   */
  readonly feedbackPeople: number;
  /** How many Triage rows are a missed session (`missedWorkout`). The greeting teases on it. */
  readonly missedSessions: number;
  /**
   * The reasons this trainer has muted at type level, as reason KEYS (R16, R18).
   *
   * Never a count — R18 states the count is deliberately not computed, because computing it would
   * mean running the muted detection. Naming the kinds IS the obligation, and a surface that
   * presents an empty queue without them is claiming "everything handled" when it cannot know.
   */
  readonly mutedReasons: readonly AttentionReason[];
  /** The figure the pulse tile over the bento prints. */
  readonly tile: AttentionTileFigure;
}

/** Which relative key to try, and the values it needs present. Most specific first. */
interface SubVariant {
  readonly key: string;
  readonly needs: readonly string[];
}

/**
 * The per-reason variants of mobile's `sub`, read off the bundle rather than guessed.
 *
 * `detail` is appended to every list by `sentenceFor`, so each of these may be empty — which is
 * what `lapsedActivity` is, deliberately (see the header).
 */
const SUB_VARIANTS: Readonly<Record<AttentionReason, readonly SubVariant[]>> = {
  missedWorkout: [{ key: 'sub', needs: ['when'] }],
  // No variant at all: the sentence names the whole event and needs no field, so `detail` — which
  // `sentenceFor` always appends — is the only thing to say.
  abandonedWorkout: [],
  lapsedActivity: [{ key: 'sub', needs: ['count'] }],
  noUpcomingSessions: [
    { key: 'sub', needs: ['plan'] },
    { key: 'subEmpty', needs: [] },
  ],
  planEndingSoon: [{ key: 'sub', needs: ['when'] }],
  planCompletionDue: [{ key: 'sub', needs: ['when'] }],
  invitationPending: [{ key: 'sub', needs: ['count'] }],
  goalRenewalDue: [{ key: 'sub', needs: ['when'] }],
  unreadMessage: [{ key: 'sub', needs: ['preview'] }],
  goalAchieved: [{ key: 'sub', needs: ['goal'] }],
  sessionAwaitingReview: [],
  planCompleted: [{ key: 'sub', needs: ['plan'] }],
  newClip: [{ key: 'sub', needs: ['exercise'] }],
  clipCommentAwaitingReply: [{ key: 'sub', needs: ['preview'] }],
  // The filler's name is already the row's name (`subject.person`); the sentence says what happened,
  // and that the address was never proven when the payload says so (R20). `unverified` is a presence
  // flag the sentence does not interpolate.
  publicFormLeadSubmitted: [{ key: 'subUnverified', needs: ['unverified'] }],
  // The server's own average, how many readings it is taken over and the level it stood under
  // (R22, R23). `count` selects the plural: one reading is not an average and its form says so.
  lowSleep: [{ key: 'sub', needs: ['value', 'count', 'level'] }],
  lowEnergy: [{ key: 'sub', needs: ['value', 'count', 'level'] }],
  // One movement named, and how many more of the session's movements hold a record (R24).
  prAchieved: [
    { key: 'subMore', needs: ['exercise', 'count', 'when'] },
    { key: 'sub', needs: ['exercise', 'when'] },
  ],
  surveyAwaitingReview: [
    { key: 'sub', needs: ['survey', 'when'] },
    { key: 'subNoWhen', needs: ['survey'] },
    { key: 'subEmpty', needs: ['when'] },
    { key: 'subEmptyNoWhen', needs: [] },
  ],
};

/** What the home needs from outside itself to word a date. */
export interface AttentionSectionDeps {
  /**
   * A civil date or an instant, worded for the reader's locale. Supplied by the screen rather than
   * computed here: the locale is i18next's, and this module does not import i18next.
   */
  readonly formatDate: (isoDate: string) => string;
  /**
   * Whole days since that client last finished a workout, or `undefined` where the roster has no
   * such day — either the client has never finished one, or the roster did not answer.
   *
   * A parameter rather than a read: this module is pure, and the roster is the screen's query.
   */
  readonly daysSinceLastWorkout: (customerId: number | null) => number | undefined;
  /**
   * The trainer's own client profile (`/auth/me`'s `customer.id`), or `null`. Its tasks never lead
   * to the chat: the backend refuses a conversation with yourself (`conversation_with_self`).
   */
  readonly selfCustomerId: number | null;
}

function number(value: number | null | undefined): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function textOrUndefined(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === '' ? undefined : trimmed;
}

/**
 * The values a reason's sentence can interpolate, with every absent one left `undefined` so the
 * variant filter can see it.
 *
 * `sessionLocalDay` is preferred over `sessionDate` for the missed session's "when": it is the
 * client's own civil day, which is the day the session was missed on (R10), while `sessionDate` is
 * an instant that resolves to the previous day for any reader west of the client.
 */
function valuesFor(
  reason: AttentionReason,
  payload: AttentionItemPayload | null | undefined,
  signalAtUtc: string | null,
  subjectCustomerId: number | null,
  deps: AttentionSectionDeps,
): Record<string, string | number | undefined> {
  const date = (iso: string | null | undefined): string | undefined => {
    const value = textOrUndefined(iso);
    return value === undefined ? undefined : deps.formatDate(value);
  };

  switch (reason) {
    case 'missedWorkout':
      return { when: date(payload?.sessionLocalDay ?? payload?.sessionDate) };
    // The session's NAME is not the event — "Siła 5×5" under a client's name says nothing about
    // what happened to it — and the row has no room to spend on it. Both sentences name the event
    // outright and interpolate nothing.
    case 'abandonedWorkout':
    case 'sessionAwaitingReview':
      return {};
    case 'noUpcomingSessions':
      return { plan: textOrUndefined(payload?.planName) };
    case 'planEndingSoon':
    case 'planCompletionDue':
      return { when: date(payload?.planEndDate) };
    case 'invitationPending':
      // `count`, not `days`: i18next selects a plural form off that name, and Polish needs three
      // ("od 1 dnia" / "od 3 dni" / "od 9 dni").
      return {
        count: typeof payload?.daysPending === 'number' ? payload.daysPending : undefined,
      };
    case 'goalRenewalDue':
      // The deadline may be days away or already past (`GoalRenewalDueDetector` has no lower
      // bound), so the sentence states the date and takes no tense on it.
      return { when: date(payload?.goalDeadline) };
    case 'goalAchieved':
      return { goal: textOrUndefined(payload?.goalDescription) };
    case 'unreadMessage':
      return { preview: textOrUndefined(payload?.messagePreview) };
    case 'surveyAwaitingReview':
      // The survey carries no submission stamp of its own, so the item's own signal instant is the
      // "when" — which is the moment the detector saw the submission, and the same thing.
      return { survey: textOrUndefined(payload?.surveyTitle), when: date(signalAtUtc) };
    case 'planCompleted':
      return { plan: textOrUndefined(payload?.planName) };
    case 'newClip':
      return { exercise: textOrUndefined(payload?.clipExerciseName) };
    case 'clipCommentAwaitingReply':
      return { preview: textOrUndefined(payload?.clipCommentPreview) };
    case 'publicFormLeadSubmitted':
      // Only an explicit `false` — absent or `null` is an older backend and says nothing.
      return { unverified: payload?.publicFormLeadEmailVerified === false ? 'unverified' : undefined };
    case 'lapsedActivity':
      // `count`, not `days`, because i18next selects a plural form off that name and Polish needs
      // three of them ("od 1 dnia" / "od 2 dni" / "od 41 dni").
      return { count: deps.daysSinceLastWorkout(subjectCustomerId) };
    case 'lowSleep':
    case 'lowEnergy':
      // Raw numbers: the sentence's own format spec prints them, in the reader's separator.
      return {
        value: number(payload?.vitalValue),
        count: number(payload?.vitalSampleCount),
        level: number(payload?.vitalThreshold),
      };
    case 'prAchieved': {
      const movements = number(payload?.prCount);
      return {
        exercise: textOrUndefined(payload?.prExerciseName),
        // The OTHER movements with a record, and `count` because the phrase is plural on it.
        count: movements === undefined || movements <= 1 ? undefined : movements - 1,
        // The client's own day the session ended on (R10), where the wire has it.
        when: date(payload?.sessionLocalDay ?? signalAtUtc),
      };
    }
  }
}

function sentenceFor(
  reason: AttentionReason,
  payload: AttentionItemPayload | null | undefined,
  signalAtUtc: string | null,
  subjectCustomerId: number | null,
  deps: AttentionSectionDeps,
): AttentionSentence {
  const values = valuesFor(reason, payload, signalAtUtc, subjectCustomerId, deps);
  const usable = SUB_VARIANTS[reason].filter((variant) =>
    variant.needs.every((need) => values[need] !== undefined),
  );
  const keys = usable.map((variant) => attentionReasonKey(reason, variant.key));
  const slots: (readonly string[])[] = usable.map((variant) => variant.needs);
  // Always last, and every reason has one: a sentence that needs no field at all, so this list can
  // never be empty and a row can never render a raw key.
  keys.push(attentionReasonKey(reason, 'detail'));
  slots.push([]);

  const filled: Record<string, string | number> = {};
  for (const [name, value] of Object.entries(values)) {
    if (value !== undefined) filled[name] = value;
  }
  return { keys, values: filled, slots };
}

/** One wire item as a row, or `null` where this build cannot name its reason. */
function itemOf(
  wireItem: AttentionQueueItem,
  deps: AttentionSectionDeps,
): AttentionEntry | null {
  const reason = attentionReasonOf(wireItem.reason);
  if (reason === null) return null;
  const anchorKey = textOrUndefined(wireItem.anchorKey);
  // The anchor is what a response suppresses (R7). An item without one cannot be responded to, so
  // drawing it would give the trainer two controls that silently do nothing.
  if (anchorKey === undefined) return null;

  const subjectCustomerId =
    typeof wireItem.subject?.customerId === 'number' ? wireItem.subject.customerId : null;
  const self = subjectCustomerId !== null && subjectCustomerId === deps.selfCustomerId;
  const signalAtUtc = textOrUndefined(wireItem.signalAtUtc) ?? null;

  return {
    category: categoryOf(wireItem.category),
    item: {
      key: `${reason}|${anchorKey}`,
      anchorKey,
      reason,
      titleKey: attentionReasonTitleKey(reason),
      subjectCustomerId,
      messageable: subjectCustomerId !== null && !self,
      workoutExecutionId:
        typeof wireItem.payload?.workoutExecutionId === 'number'
          ? wireItem.payload.workoutExecutionId
          : null,
      signalAtUtc,
      fullName: textOrUndefined(wireItem.subject?.person?.fullName) ?? '',
      initials: textOrUndefined(wireItem.subject?.person?.initials) ?? '',
      avatarUrl: textOrUndefined(wireItem.subject?.person?.avatarUrl) ?? null,
      description: sentenceFor(reason, wireItem.payload, signalAtUtc, subjectCustomerId, deps),
      glyph: taskGlyph(reason),
      action: taskAction(reason, wireItem.payload, subjectCustomerId, self),
      detail: feedbackShape(reason, wireItem.payload),
      triage: triageShape(wireItem.payload),
    },
  };
}


/**
 * Which half of the queue an item belongs to (R14).
 *
 * The BACKEND's own word, narrowed. Anything it sends that this build has no arm for lands on
 * `other`, which every surface then skips — the same forward-compatible drop `attentionReasonOf`
 * applies to the reason.
 */
export type AttentionCategory = 'Triage' | 'Feedback' | 'Win' | 'other';

/** One item and the category the server put it in. */
export interface AttentionEntry {
  readonly category: AttentionCategory;
  readonly item: AttentionItem;
}

/** The half of the queue a task is WORKED in, on every surface that draws tasks. */
export type TaskSection = 'Triage' | 'Feedback';

/**
 * Which half an entry is worked in, or `null` where it is not a task on any surface.
 *
 * The category decides (R14) — except for the personal record. Its category is `Win`, because it is
 * a moment to congratulate rather than work to resolve, and R24 still makes it "a task like any
 * other on every surface that presents the queue". So it is admitted BY REASON and stands with the
 * tasks: in the Pulpit's „Zadania", in `/zadania`'s deck and its „Zadania" scope, in the client's
 * Talia, and in every count taken over them. `GoalAchieved`, the other Win, stays out exactly as
 * before — it is the activity feed's.
 */
export function taskSectionOf(entry: AttentionEntry): TaskSection | null {
  if (entry.category === 'Triage' || entry.category === 'Feedback') return entry.category;
  return entry.category === 'Win' && entry.item.reason === 'prAchieved' ? 'Triage' : null;
}

/**
 * The whole queue as ONE list, in the server's own order, each item carrying its category.
 *
 * `/zadania` works the queue as a single deck — severity first, then recency (R8, `RankAndCap`) —
 * and the Pulpit's two-card split is derived from this rather than parsed separately, so the two
 * screens can never disagree about which items exist or what one of them says.
 */
export interface AttentionQueueProjection {
  readonly entries: readonly AttentionEntry[];
  /** See `AttentionSections.mutedReasons`. Never a count (R18). */
  readonly mutedReasons: readonly AttentionReason[];
}

/**
 * The reasons this trainer has muted (R16, R18), and nothing else.
 *
 * Its own function because it is the one part of the queue's projection that depends on NOTHING but
 * `mutedReasons` — no payload, no formatter, no roster. `/zadania`'s foot names them on every
 * render, and reaching them through `attentionQueue` meant rebuilding every worded sentence in the
 * queue to read a list of at most fifteen strings.
 *
 * Never a count. R18 states the count is deliberately not computed, because computing it would mean
 * running the muted detection; naming the kinds IS the obligation.
 */
export function mutedReasonsOf(
  queue: AttentionQueueResponse | null | undefined,
): readonly AttentionReason[] {
  const muted: AttentionReason[] = [];
  for (const wireReason of queue?.mutedReasons ?? []) {
    const reason = attentionReasonOf(wireReason);
    if (reason !== null) muted.push(reason);
  }
  return muted;
}

function categoryOf(wire: string | null | undefined): AttentionCategory {
  switch (wire) {
    case 'Triage':
    case 'Feedback':
    case 'Win':
      return wire;
    default:
      return 'other';
  }
}

/**
 * Every nameable item in the queue, flat and in arrival order, plus the muted reasons.
 *
 * `queue` is `null` when the read failed or has not landed; the list is then empty and
 * `mutedReasons` with it — a failed queue costs the trainer their task list, never the screen.
 */
export function attentionQueue(
  queue: AttentionQueueResponse | null | undefined,
  deps: AttentionSectionDeps,
): AttentionQueueProjection {
  const entries: AttentionEntry[] = [];
  for (const wireItem of queue?.items ?? []) {
    const built = itemOf(wireItem, deps);
    if (built !== null) entries.push(built);
  }

  return { entries, mutedReasons: mutedReasonsOf(queue) };
}

/**
 * Split the queue into the Pulpit's two cards.
 *
 * `queue` is `null` when that read failed or has not landed. Both sections then come back empty and
 * `mutedReasons` with them — a failed queue costs the trainer their task list, never the screen,
 * which is how the roster already degrades.
 */
export function attentionSections(
  queue: AttentionQueueResponse | null | undefined,
  deps: AttentionSectionDeps,
): AttentionSections {
  const { entries, mutedReasons } = attentionQueue(queue, deps);
  const tasks: AttentionItem[] = [];
  const feedback: AttentionItem[] = [];

  for (const entry of entries) {
    const section = taskSectionOf(entry);
    if (section === 'Feedback') feedback.push(entry.item);
    else if (section === 'Triage') tasks.push(entry.item);
    // Anything else falls through — see `taskSectionOf`.
  }

  return {
    tasks,
    tasksTotal: tasks.length,
    feedback,
    feedbackTotal: feedback.length,
    feedbackPeople: new Set(
      feedback.map((item) => (item.subjectCustomerId === null ? item.key : item.subjectCustomerId)),
    ).size,
    missedSessions: tasks.filter((item) => item.reason === 'missedWorkout').length,
    mutedReasons,
    tile: {
      total: tasks.length + feedback.length,
      triage: tasks.length,
      feedback: feedback.length,
      dots: Math.min(tasks.length + feedback.length, TILE_DOTS),
    },
  };
}
